/*
 * Copyright (C) 2018 - 2026 Entgra (Pvt) Ltd, Inc - All Rights Reserved.
 *
 * Unauthorised copying/redistribution of this file, via any medium is strictly prohibited.
 *
 * Licensed under the Entgra Commercial License, Version 1.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *       https://entgra.io/licenses/entgra-commercial/1.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button } from 'antd';
import { CameraOutlined } from '@ant-design/icons';
import styles from './styles.module.css';

const CameraCapture = ({ onCapture }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [hasPermission, setHasPermission] = useState(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState('');

  useEffect(() => {
    const startCamera = async () => {
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          setCameraError('Camera is not supported by this browser.');
          setHasPermission(false);
          return;
        }

        let stream;

        try {
          stream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: { ideal: 'environment' } },
            audio: false,
          });
        } catch (error) {
          stream = await navigator.mediaDevices.getUserMedia({
            video: true,
            audio: false,
          });
        }

        streamRef.current = stream;
        setHasPermission(true);
      } catch (error) {
        setCameraError(
          'Camera access denied or blocked. Use HTTPS or localhost and allow camera permission.',
        );
        setHasPermission(false);
      }
    };

    startCamera();

    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    const stream = streamRef.current;

    if (!video || !stream || hasPermission !== true) {
      return;
    }

    video.srcObject = stream;

    const onLoadedMetadata = () => {
      video
        .play()
        .then(() => {
          setCameraReady(true);
          setCameraError('');
        })
        .catch(() => {
          setCameraError('Camera preview could not start. Please try again.');
        });
    };

    video.addEventListener('loadedmetadata', onLoadedMetadata);

    if (video.readyState >= 1) {
      onLoadedMetadata();
    }

    return () => {
      video.removeEventListener('loadedmetadata', onLoadedMetadata);
    };
  }, [hasPermission]);

  const captureImage = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;

    if (
      !cameraReady ||
      !video ||
      !canvas ||
      !video.videoWidth ||
      !video.videoHeight
    ) {
      setCameraError('Camera is still starting. Please try again.');
      return;
    }

    const context = canvas.getContext('2d');

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    onCapture(canvas.toDataURL('image/jpeg'));
  };

  if (hasPermission === false) {
    return (
      <Alert type="error" showIcon message={cameraError || 'Camera failed.'} />
    );
  }

  if (hasPermission === null) {
    return <Alert type="info" showIcon message="Requesting camera access..." />;
  }

  return (
    <div className={styles.cameraCapture}>
      <div className={styles.cameraPanel}>
        <div className={styles.videoFrame}>
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            className={styles.video}
          />
          {!cameraReady && (
            <div className={styles.cameraOverlay}>Starting camera...</div>
          )}
        </div>
      </div>

      <canvas ref={canvasRef} className={styles.canvas} />
      {cameraError && <Alert type="warning" showIcon message={cameraError} />}

      <div className={styles.actionRow}>
        <Button
          type="primary"
          icon={<CameraOutlined />}
          onClick={captureImage}
          disabled={!cameraReady}
          className={styles.captureBtn}
        >
          Take Photo
        </Button>
      </div>
    </div>
  );
};

export default CameraCapture;
